import * as angular from 'angular';
import * as _ from 'lodash';
import * as d3 from 'd3';

import { IFileManagementService, ISeriesService, IDashboardLoadService, IDateRangeService, IFlowChartService, INameBuilderService, IDigitalSetHelperService, IStringValuesHelperService } from '../../services';
import { editWidgetState, editGroupWidgetState, missingSeries, infoBackgroundColor, infoBorderColor, infoTextColor, warningBorderColor, warningTextColor, errorBorderColor, errorTextColor, errorBackgroundColor, warningBackgroundColor } from '../../common/constants';
import { ComponentDefinition, WidgetValues, ErrorLevel, TagDateValue, Dashboard, IValueViewModel, Dictionary, DigitalSetSimpleDictionary, ActualDataType, TagStringValueDto } from '../../models';
import { FlowChartSeries, IIdentifierOverlay } from '../../widgets';
import { FlowChartWidget } from './flowChart.widget';
import { IRootScope } from 'IRootScope';
import { IOverlayModel } from 'framework';
import { groupSelected, refreshClick } from '../../common/events';
import { getUnitName, toDictionary, WideError } from '../../common';

class FlowChartController implements angular.IComponentController {
    backgroundContent: string;
    boundData: WidgetValues;
    displayedSeries: IIdentifierOverlay[];
    isBackgroundLoading: boolean;
    isGridVisible: boolean;
    isLoading: boolean;
    isReadOnly: boolean;
    isSeriesLoading: boolean;
    isZoomEnabled: boolean;
    item: FlowChartWidget;
    loadingMessage: string;

    readonly dashboards: Dashboard[];
    readonly digitalSets: DigitalSetSimpleDictionary;
    readonly isEditMode: boolean;
    readonly isBound: boolean;
    readonly series: IIdentifierOverlay[];
    readonly stringValues: TagStringValueDto[];
    readonly viewModels: Dictionary<IValueViewModel>;

    private _isEditing: boolean;
    private _seriesConfiguration: FlowChartSeries[];

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        'fileManagementService',
        'seriesService',
        'dashboardLoadService',
        'dateRangeService',
        'digitalSetHelperService',
        'flowChartService',
        'nameBuilderService',
        'stringValuesHelperService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _fileManagementService: IFileManagementService,
        private _seriesService: ISeriesService,
        private _dashboardLoadService: IDashboardLoadService,
        private _dateRangeService: IDateRangeService,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _flowChartService: IFlowChartService,
        private _nameBuilderService: INameBuilderService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
    }

    $onInit() {
        this._isEditing = this.shouldShowGrid();
        this.isZoomEnabled = this._isEditing;

        // asociates methods to handle zoom in widget to bind button
        (this.item as any).toggleEnableZoom = () => {
            this.isZoomEnabled = !this.isZoomEnabled;
            this.item.configuration.isZoomEnabled = this.isZoomEnabled;
        };

        (this.item as any).fitZoom = () => {
            this._$rootScope.$broadcast('wt-background-overlay-fit');
        };

        this._$scope.$watch(() => this._$rootScope.isEditMode, () => {
            this._isEditing = this.shouldShowGrid();
            this.isGridVisible = this._isEditing && this.item.configuration.showGrid;
            this.isReadOnly = !this._isEditing;
            if (!this.isBound) {
                this.updateSeries(this.item.configuration.series);
            }
        });
        if (!this.isBound) {
            this._$scope.$watch(() => this.item.configuration.series, (newValue: FlowChartSeries[]) => {
                if (newValue) {
                    this.updateSeries(newValue);
                }
            }, true);

            this._$scope.$on(groupSelected, () => {
                this.updateSeries(this.item.configuration.series);
            });
        }
        this.isGridVisible = this._isEditing;
        this._$scope.$watch(
            () => this.item.configuration.backgroundUrl,
            (newValue: string) => {
                if (newValue && !this.isBackgroundLoading) {
                    this.isBackgroundLoading = true;
                    this._fileManagementService
                        .getFileFromContentAsUrl(`Content\\${newValue}`)
                        .then(data => this.backgroundContent = data)
                        .catch(reason => console.error('an error occured'))
                        .finally(() => this.isBackgroundLoading = false);
                }
            });

        this._$scope.$watch(() => this.item.configuration.showGrid, (newValue: boolean) => {
            this.isGridVisible = this.shouldShowGrid() && this.item.configuration.showGrid;
        });

        if (this.isBound && this.boundData) {
            this._$scope.$watch(() => this.boundData, (newValue) => {
                this.update(newValue);
            });
        } else if (!this.isBound && !this.boundData) {
            this._$scope.$on(refreshClick, () => {
                this.isSeriesLoading = true;
                this.refreshValues()
                    .finally(() => this.isSeriesLoading = false);
            });
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['series']?.currentValue) {
            this.displayedSeries = this.series;
            if (this.isBound && this.boundData) {
                this.update(this.boundData);
            } else if (!this.isBound && !this.boundData) {
                this.isSeriesLoading = true;
                this.refreshValues()
                    .finally(() => this.isSeriesLoading = false);
            }
        }
    }

    private shouldShowGrid(): boolean {
        // TODO hard coded state? really?!?
        return (this._$state.current.name === editWidgetState
            || this._$state.current.name === editGroupWidgetState)
            && !!this._$rootScope.isEditMode;
    }

    private refreshValues(): angular.IPromise<any> {
        const endDate = this._dateRangeService.endDate() as string;
        const beginDate = this._dateRangeService.beginDate() as string;
        const config = this.item.getReadConfiguration(beginDate, endDate);
        return this._dashboardLoadService
            .readValues([config], beginDate, endDate)
            .then(values => this.update(values[0]));

    }

    private updateSeries(newValue: FlowChartSeries[]) {
        if (newValue) {
            this.isSeriesLoading = true;
            this._seriesService
                .getUpdatedGuids(newValue)
                .then(data => {
                    this._seriesConfiguration = data;
                    return this.updateSeriesDisplay(this._seriesConfiguration);
                })
                .finally(() => this.isSeriesLoading = false);
        }
    }

    private updateSeriesDisplay(newValue: FlowChartSeries[]) {
        const dashboards = toDictionary(this.dashboards);
        this.displayedSeries = newValue.map(d => {
            const vvm = this.viewModels[d.guid];
            if (!vvm) {
                this.item.setError(new WideError(ErrorLevel.Error, missingSeries));
            } else {
                const series = this._flowChartService
                    .createOverlay(d, vvm, dashboards, this._$scope, this._isEditing);
                if (this.isEditMode) {
                    series.attributes = {};
                }
                return series;
            }
        });

        if (this.isBound && this.boundData) {
            this.update(this.boundData);
        } else if (!this.isBound && !this.boundData) {
            return this.refreshValues();
        }
    }

    private update(data: WidgetValues) {
        if (this.displayedSeries && data) {
            this.displayedSeries.forEach(s => this.updateDisplayedSeries(s, data));
        }
    }

    private updateDisplayedSeries(s: IIdentifierOverlay, data: WidgetValues) {
        const found = _.find(data.aggregatedValue, (v: TagDateValue) => v.guid === s.guid && v.valueType === s.valueType);
        const quality = _.find(data.statuses, v => v.key === s.guid);
        const alarmLevel = _.find(data.alarms, v => v.key === s.guid);
        let guid = s.guid;

        if (this.item.map) {
            for (const key of Object.keys(this.item.map)) {
                if (this.item.map[key] === guid) {
                    guid = key;
                    break;
                }
            }
        }

        let series = this.item.configuration.series;
        if (this._seriesConfiguration) {
            series = this._seriesConfiguration;
        }
        const config = _.find(series, ss => guid === ss.guid && s.valueType === ss.value);
        if (alarmLevel) {
            this.overrideColorForAlarm(s, alarmLevel ? alarmLevel.value : 0);
        }

        if (found) {
            const mode = this.item.configuration.aggregation.mode;
            const foundVvm = _.find(this.viewModels, vm => vm.guid === s.guid);
            const decimals = config && (config.decimals || config.decimals === 0)
                ? config.decimals
                : 2;
            const format = d3.format(`2.${decimals}f`);

            const value = foundVvm?.actualDataType === ActualDataType.String
                ? this._stringValuesHelperService.getValueOrString(foundVvm, found, this.stringValues, mode)
                : this._digitalSetHelperService.getValueOrDigital(foundVvm, found, this.digitalSets, mode);
            s.value = (foundVvm?.actualDataType === ActualDataType.Digital || foundVvm?.actualDataType === ActualDataType.String)
                ? value as string
                : format(value as number);

            if (s.showQuality) {
                s.quality = quality ? quality.value : 0;
            }
        } else {
            s.value = '-';
            if (s.showQuality) {
                s.quality = 2;
            }
        }

        let name = s.showValue ? s.value : '';
        if (config) {
            if (config.showUnit && s.unitName) {
                s.unitName = getUnitName(s.unitName, config.value);
                name = this._nameBuilderService.getNameWithUnit(name, s.unitName, config.value);
            }
        }
        s.value = name;
    }

    private overrideColorForAlarm(s: IOverlayModel, alarmLevel: number) {
        switch (alarmLevel) {
            case 1:
                s.backgroundColor = infoBackgroundColor;
                s.borderColor = infoBorderColor;
                s.textColor = infoTextColor;
                break;
            case 2:
                s.backgroundColor = warningBackgroundColor;
                s.borderColor = warningBorderColor;
                s.textColor = warningTextColor;
                break;
            case 3:
                s.backgroundColor = errorBackgroundColor;
                s.borderColor = errorBorderColor;
                s.textColor = errorTextColor;
                break;
        }
    }
}

export const wtFlowChart: ComponentDefinition = {
    componentId: 'wtFlowChart',
    controller: FlowChartController,
    template: require('./flowChart.component.html'),
    controllerAs: 'vm',
    bindings: {
        item: '=',
        boundData: '=?',
        dashboards: '<',
        digitalSets: '<',
        isEditMode: '<?',
        isZoomEnabled: '=?',
        isBound: '<?',
        series: '<?',
        stringValues: '<',
        viewModels: '<'
    }
};